const AudioContext = window.AudioContext || window.webkitAudioContext;
let _ctx = null;
const getCtx = () => {
  if (!_ctx) _ctx = new AudioContext();
  if (_ctx.state === 'suspended') _ctx.resume();
  return _ctx;
};

export const SOUND_NAMES = ['Default', 'Slack', 'WhatsApp', 'Chime', 'Bell', 'Bubble', 'Ping', 'Alert', 'None'];

const SOUNDS = {
  'Default': (ctx) => {
    // Slack-style two-tone ding — clear and audible
    const t = ctx.currentTime;
    [
      { freq: 1047, start: 0, dur: 0.3, gain: 0.5 },
      { freq: 1319, start: 0.12, dur: 0.3, gain: 0.5 },
    ].forEach(({ freq, start, dur, gain }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.value = freq;
      g.gain.setValueAtTime(0, t + start);
      g.gain.linearRampToValueAtTime(gain, t + start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + start + dur);
      osc.start(t + start); osc.stop(t + start + dur);
    });
  },
  'Slack': (ctx) => {
    // Classic Slack knock sound
    const t = ctx.currentTime;
    const freqs = [783, 1047, 1319];
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.value = freq;
      g.gain.setValueAtTime(0, t + i * 0.1);
      g.gain.linearRampToValueAtTime(0.4, t + i * 0.1 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.1 + 0.25);
      osc.start(t + i * 0.1); osc.stop(t + i * 0.1 + 0.25);
    });
  },
  'WhatsApp': (ctx) => {
    // WhatsApp message received sound
    const t = ctx.currentTime;
    const notes = [1047, 1319, 1047, 1568];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.value = freq;
      g.gain.setValueAtTime(0, t + i * 0.07);
      g.gain.linearRampToValueAtTime(0.35, t + i * 0.07 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.15);
      osc.start(t + i * 0.07); osc.stop(t + i * 0.07 + 0.15);
    });
  },
  'Chime': (ctx) => {
    // Gentle melodic chime
    const t = ctx.currentTime;
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.value = freq;
      g.gain.setValueAtTime(0, t + i * 0.12);
      g.gain.linearRampToValueAtTime(0.3, t + i * 0.12 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.4);
      osc.start(t + i * 0.12); osc.stop(t + i * 0.12 + 0.4);
    });
  },
  'Bell': (ctx) => {
    // Deep resonant bell
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g); osc2.connect(g); g.connect(ctx.destination);
    osc.type = 'sine'; osc.frequency.value = 440;
    osc2.type = 'sine'; osc2.frequency.value = 880;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.6, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + 1.2);
    osc.start(t); osc.stop(t + 1.2);
    osc2.start(t); osc2.stop(t + 0.6);
  },
  'Bubble': (ctx) => {
    // Soft bubble pop
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g); g.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.1);
    g.gain.setValueAtTime(0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.start(t); osc.stop(t + 0.15);
  },
  'Ping': (ctx) => {
    // High clear ping
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g); g.connect(ctx.destination);
    osc.type = 'sine'; osc.frequency.value = 1760;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.start(t); osc.stop(t + 0.5);
  },
  'Alert': (ctx) => {
    // Urgent double beep
    const t = ctx.currentTime;
    [0, 0.2].forEach(delay => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'square'; osc.frequency.value = 880;
      g.gain.setValueAtTime(0, t + delay);
      g.gain.linearRampToValueAtTime(0.3, t + delay + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + delay + 0.15);
      osc.start(t + delay); osc.stop(t + delay + 0.15);
    });
  },
  'None': null,
};

export const playNotifSound = (soundName = 'Default') => {
  try {
    const fn = SOUNDS[soundName];
    if (!fn) return;
    const ctx = getCtx();
    fn(ctx);
  } catch(e) { console.warn('Sound play failed:', e); }
};

export const unlockAudio = () => {
  try { getCtx(); } catch(e) {}
};
